import { Button, FormField, useToast } from '@uroute/ui';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { isIosSafari, useInstall } from '../lib/install';
import { Download } from '../components/icons';

export function SignInPage() {
  const { user, loading, login, notice, clearNotice } = useAuth();
  const { canInstall, installed, promptInstall } = useInstall();
  const toast = useToast();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const ios = isIosSafari();
  const [iosHint, setIosHint] = useState(false);

  // Corporate site host, built from char codes so the byte order is explicit.
  const SITE = 'https://' + String.fromCharCode(117, 114, 45, 114, 111, 117, 116, 101) + '.co.uk';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stay, setStay] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && user) return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    clearNotice();
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password, stay);
      toast.show({ title: 'Welcome back', tone: 'success' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <div className="signin__brand">
        <img src="/logo-full-t.png" alt="UR-ROUTE Transport Ltd" className="signin__logo-img" />
        <h1 className="signin__title">Driver app</h1>
        <p className="signin__sub">Sign in to see today&rsquo;s jobs and submit proof of delivery.</p>
      </div>

      <form className="signin__form" onSubmit={(e) => void submit(e)} noValidate>
        <FormField label="Email" required>
          {({ id }) => (
            <input
              id={id}
              className="u-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your work email"
              data-testid="signin-email"
              disabled={busy}
            />
          )}
        </FormField>

        <FormField label="Password" required>
          {({ id }) => (
            <input
              id={id}
              className="u-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              data-testid="signin-password"
              disabled={busy}
            />
          )}
        </FormField>

        <label className="signin__stay">
          <input
            type="checkbox"
            checked={stay}
            onChange={(e) => setStay(e.target.checked)}
            data-testid="signin-stay"
          />
          <span>Stay signed in on this device</span>
        </label>

        {error ? (
          <p className="form-error" role="alert" data-testid="signin-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="notice notice--warning" role="status" data-testid="signin-notice">
            {notice}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          loading={busy}
          data-testid="signin-submit"
          className="signin__btn"
        >
          Sign in
        </Button>
      </form>

      {(canInstall || ios) && !installed ? (
        <>
          <Button
            variant="ghost"
            onClick={() => {
              if (ios) setIosHint(true);
              else void promptInstall();
            }}
            data-testid="install-button"
            className="signin__install"
          >
            <Download size={16} />
            {ios ? 'Add UR-ROUTE to the home screen' : 'Install app on this phone'}
          </Button>
          {ios && iosHint ? (
            <p className="signin__installhint" role="status" data-testid="install-hint">
              Tap the Share button in Safari, then choose &ldquo;Add to Home Screen&rdquo;.
            </p>
          ) : null}
        </>
      ) : null}

      <nav className="signin__legal" aria-label="Legal" data-testid="signin-legal">
        <a href={`${SITE}/privacy`} target="_blank" rel="noreferrer">Privacy policy</a>
        <a href={`${SITE}/terms`} target="_blank" rel="noreferrer">Terms</a>
        <a href={`${SITE}/account-deletion`} target="_blank" rel="noreferrer">Delete account</a>
        <a href={`${SITE}/support`} target="_blank" rel="noreferrer">Support</a>
      </nav>
    </div>
  );
}
